"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSidebar } from "@/components/ui/sidebar";
import { useEditorLeaveStore } from "@/utils/editor-unsaved-guard";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";

export function EditorUnsavedChangesDialog() {
  const { setOpenMobile } = useSidebar();
  const open = useEditorLeaveStore((state) => state.open);
  const answerLeave = useEditorLeaveStore((state) => state.answerLeave);
  const experience = useEditorLeaveStore((state) => state.experience);
  // Saving the experience leaves inline settings drafts behind, so it is only
  // offered when nothing else is unsaved.
  const hasDirtyDrafts = useEditorLeaveStore((state) =>
    [...state.drafts.values()].some((draft) => draft.dirty),
  );
  const save = hasDirtyDrafts ? undefined : experience?.save;
  const [saving, setSaving] = useState(false);

  const leave = () => {
    setOpenMobile(false);
    answerLeave(true);
  };

  const handleSave = async () => {
    if (!save) return;
    setSaving(true);
    try {
      // A failed save shows its own error and keeps the editor open.
      if (await save.run()) leave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !saving) answerLeave(false);
      }}
    >
      <DialogContent
        overlayClassName="z-[100] bg-foreground/40"
        className="top-12 z-[100] max-w-md translate-y-0 gap-5 rounded-2xl p-6"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex items-start gap-4 pr-6">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <TriangleAlert className="size-5" aria-hidden />
          </span>
          <div className="flex min-w-0 flex-col gap-1.5">
            <DialogTitle className="text-lg">Unsaved changes</DialogTitle>
            <DialogDescription className="text-sm">
              {experience
                ? `The ${experience.kind} “${experience.name}” has changes that aren't saved.`
                : "This experience has changes that aren't saved."}
            </DialogDescription>
          </div>
        </div>
        {save ? (
          // Closing the dialog keeps editing.
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={saving} onClick={leave}>
              Discard
            </Button>
            <Button disabled={saving} onClick={() => void handleSave()}>
              {saving ? "Saving…" : save.label}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={leave}>
              Discard
            </Button>
            <Button onClick={() => answerLeave(false)}>Keep editing</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
